import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowRight, Shield, Globe, GraduationCap } from "lucide-react";
import { HERO } from "@/constants/testIds";
import { AffirmationOfTheDay } from "@/components/AffirmationOfTheDay";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: "easeOut" },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

// Trust Bar - Top 4 Accreditations
const trustBadges = [
  { icon: Shield, label: "ADOS-2 Certified", type: "icon" },
  { image: "https://customer-assets.emergentagent.com/job_modern-minds-landing/artifacts/aohrd6eo_Badge%201.jpeg", label: "Evergreen ASDI", type: "image" },
  { icon: Globe, label: "BPS / APA", type: "icon" },
  { icon: GraduationCap, label: "University of Sydney", type: "icon" },
];

export const HomePage = () => {
  return (
    <div>
      <Helmet>
        <title>Modern Minds | Centre for Child Psychology, Behavioural Health & Parenting Consultancy in Lucknow</title>
        <meta name="description" content="Modern Minds offers expert child psychology services, neurodevelopmental assessments (ADOS-2, WISC), behavioural therapy, and parenting consultancy in Gomti Nagar, Lucknow. Data-driven care for neurodivergent children and families." />
        <link rel="canonical" href="https://www.modernminds.co.in/" />
      </Helmet>

      {/* SECTION 1: HERO BANNER */}
      <section data-testid={HERO.section} className="relative">
        {/* Banner Image */}
        <div className="w-full">
          <h1 className="sr-only">Modern Minds — Centre for Child Psychology, Behavioural Health & Parenting Consultancy in Lucknow</h1>
          <img
            src="https://customer-assets.emergentagent.com/job_modern-minds-landing/artifacts/p6eqzv03_Modern%20Minds%20website%20banner.jpeg"
            alt="Modern Minds - Centre for Child Psychology, Behavioural Health & Parenting Consultancy"
            className="w-full h-auto object-cover"
          />
        </div>
      </section>

      {/* CTA Buttons Section */}
      <section className="py-8 sm:py-12">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <a
              href="https://consult.modernminds.co.in"
              target="_blank"
              rel="noopener noreferrer"
              data-testid={HERO.primaryCta}
              className="inline-flex items-center justify-center gap-2 btn-gold rounded-lg px-8 py-4 font-body font-semibold text-base"
            >
              Schedule Diagnostic Mapping
              <ArrowRight size={18} />
            </a>
            <Link
              to="/assessments"
              data-testid={HERO.secondaryCta}
              className="inline-flex items-center justify-center gap-2 btn-outline rounded-lg px-8 py-4 font-body font-medium text-base"
            >
              The Grassroots Protocol
            </Link>
          </motion.div>
        </div>
      </section>

      {/* AFFIRMATION OF THE DAY */}
      <AffirmationOfTheDay variant="section" />

      {/* SECTION 2: THE EMOTIONAL HOOK */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="emotional-hook p-8 sm:p-12 lg:p-16"
          >
            <div className="gradient-divider mx-auto mb-8" />
            <p className="font-body text-base sm:text-lg lg:text-xl text-[#E2E8F0] leading-relaxed text-center">
              <span className="italic">&quot;When the noise of daily life feels like a constant storm, your child&apos;s behavior is often their only language.&quot;</span>
              <span className="block mt-4">
                We do not guess; we measure.
              </span>
              <span className="block mt-4 text-white font-medium">
                By identifying the root cause of the friction in your home, we provide a data-driven roadmap to restore calm, structure, and connection.
              </span>
              <span className="block mt-6 text-[#C9A962] font-bold">
                You are not failing. You are simply navigating without a map.
              </span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* SECTION 3: THE DUAL GATEWAY */}
      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10"
          >
            <div className="gradient-divider mx-auto mb-4" />
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3">
              Modern Minds Clinical Pathways
            </h2>
            <p className="font-body text-sm sm:text-base text-[#94A3B8]">
              Select your diagnostic journey
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {/* Pathway 1: Clinical Mapping & Precision Intervention */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="triage-card triage-gold"
            >
              <span className="inline-block bg-[#C9A962]/20 text-[#C9A962] text-xs font-body font-semibold px-3 py-1 rounded mb-4">
                PATHWAY 1
              </span>
              <h3 className="font-heading text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-3">
                Clinical Mapping & Precision Intervention
              </h3>
              <p className="font-body text-xs text-[#94A3B8] uppercase tracking-wider mb-4">
                The Workflow
              </p>
              
              {/* Visual Workflow Steps */}
              <div className="space-y-3 mb-6">
                {[
                  { step: 1, label: "Rapport-Based Intake" },
                  { step: 2, label: "Differential Diagnosis" },
                  { step: 3, label: "Psychometric Assessment" },
                  { step: 4, label: "Customized Behavioral Intervention Plan (BIP)" },
                  { step: 5, label: "Longitudinal Progress Monitoring" },
                ].map((item, index) => (
                  <div key={item.step} className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-full bg-[#C9A962] flex items-center justify-center flex-shrink-0">
                      <span className="font-body text-xs font-bold text-[#0D1B2A]">{item.step}</span>
                    </div>
                    <div className="flex-1 h-px bg-gradient-to-r from-[#C9A962]/50 to-transparent"></div>
                    <span className="font-body text-sm text-[#E2E8F0] text-right flex-shrink-0 max-w-[70%]">{item.label}</span>
                  </div>
                ))}
              </div>

              <div className="glass-card p-4 mb-6">
                <p className="font-body text-xs text-[#C9A962] uppercase tracking-wider mb-2">The Goal</p>
                <p className="font-body text-sm text-[#E2E8F0] leading-relaxed">
                  Establishing the diagnostic foundation. We don&apos;t just assess; we build a data-driven roadmap tailored to your child&apos;s specific neuro-profile, adjusting the intervention as your child evolves.
                </p>
              </div>
              <Link
                to="/assessments"
                className="inline-flex items-center gap-2 btn-gold rounded-lg px-6 py-3 font-body font-semibold text-sm"
              >
                View Assessment Battery
                <ArrowRight size={16} />
              </Link>
            </motion.div>

            {/* Pathway 2: Family System Architecture & Generational Healing */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="triage-card triage-sage"
            >
              <span className="inline-block bg-[#7C9885]/20 text-[#7C9885] text-xs font-body font-semibold px-3 py-1 rounded mb-4">
                PATHWAY 2
              </span>
              <h3 className="font-heading text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-3">
                Family System Architecture & Generational Healing
              </h3>
              <p className="font-body text-xs text-[#94A3B8] uppercase tracking-wider mb-4">
                The Workflow
              </p>
              
              {/* Visual Workflow Steps */}
              <div className="space-y-3 mb-6">
                {[
                  { step: 1, label: "Systemic Origin Analysis" },
                  { step: 2, label: "Intergenerational/Trauma-Informed Assessment" },
                  { step: 3, label: "Parenting Cycle Disruption" },
                  { step: 4, label: "Practical Tool-kits (ABA Diaries/Guidelines)" },
                ].map((item, index) => (
                  <div key={item.step} className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-full bg-[#7C9885] flex items-center justify-center flex-shrink-0">
                      <span className="font-body text-xs font-bold text-[#0D1B2A]">{item.step}</span>
                    </div>
                    <div className="flex-1 h-px bg-gradient-to-r from-[#7C9885]/50 to-transparent"></div>
                    <span className="font-body text-sm text-[#E2E8F0] text-right flex-shrink-0 max-w-[70%]">{item.label}</span>
                  </div>
                ))}
              </div>

              <div className="glass-card p-4 mb-6">
                <p className="font-body text-xs text-[#7C9885] uppercase tracking-wider mb-2">The Goal</p>
                <p className="font-body text-sm text-[#E2E8F0] leading-relaxed">
                  Breaking the cycle. We address your parental nervous system and intergenerational patterns because an unhealed parent cannot fully support a child&apos;s regulation. This pathway provides both practical guidelines (tools for home) and individual counseling for the parent, ensuring your home is a stable, neuro-affirming foundation.
                </p>
              </div>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 btn-sage rounded-lg px-6 py-3 font-body font-semibold text-sm"
              >
                Explore Therapeutic Pathways
                <ArrowRight size={16} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 4: TRUST BAR */}
      <section className="py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-8"
          >
            <p className="font-body text-xs text-[#94A3B8] uppercase tracking-widest mb-2">
              Clinical Accreditations
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="trust-bar"
          >
            {trustBadges.map((badge) => (
              <div key={badge.label} className="trust-bar-item">
                {badge.type === "image" ? (
                  <img 
                    src={badge.image} 
                    alt={badge.label}
                    className="w-12 h-12 object-contain rounded-full"
                  />
                ) : (
                  <badge.icon size={24} className="text-white/70" />
                )}
                <span className="font-body text-sm text-white">{badge.label}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
};
